/**
 * PeerFinancialTable — read-only comparison table for Step 3.
 *
 * Renders below the existing competitor-card grid. Columns: name | position |
 * revenue | gross margin | operating margin | period. The data is pulled
 * straight from the existing `competitor.financials` field (populated by the
 * backend's `market_leader_to_wizard_competitor` when a leader is linked to
 * Company Research).
 *
 * Empty state per row: grayed-out cells + an "Add to Company Research →"
 * button that deep-links to the industry detail page (same tab) so the user
 * can run the add flow from the competitive-landscape view.
 *
 * Does NOT mutate the wizard store.
 */
import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, ExternalLink, Plus } from 'lucide-react';
import Card from '../ui/Card';
import Tooltip from '../ui/Tooltip';
import type { CompetitorData } from '../../stores/wizardStore';
import { formatCurrencyValue, regionFromCountry } from '../financial/utils/formatters';
import { useIndustryBenchmarks } from '../../lib/api/industryResearchApi';
import type { CompanyFinancialSummary } from '../../types/industryResearch';

interface PeerFinancialTableProps {
  competitors: CompetitorData[];
  focalFinancials?: {
    revenue?: number | null;
    grossMargin?: number | null;
    operatingMargin?: number | null;
    period?: string | null;
  } | null;
  focalName?: string;
  industryId?: string | null;
  /**
   * Country selected in Step 1 ('US' | 'IN' | 'UK'). Drives currency symbol
   * and unit on all revenue cells (focal + peer + average row). Same-sector
   * peers are virtually always in the same country as the focal company in
   * this app, so a single country value is sufficient.
   */
  country?: string | null;
}

function fmtLarge(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}

function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  // Round to 2 decimals; round-down to integer if the result is whole.
  const rounded = Math.round(value * 100) / 100;
  return `${rounded}%`;
}

// Mirrors the legal-suffix / punctuation normalization used by
// IndustryFinancialBenchmarks so peer rows match the backend's
// company_details entries even with formatting differences (e.g.
// "Reliance Industries Ltd." vs "Reliance Industries").
const LEGAL_SUFFIX_REGEX =
  /\b(ltd|limited|inc|incorporated|corp|corporation|plc|llc|pvt|private)\b/gi;

function normalizeCompanyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[.,'’`()-]/g, ' ')
    .replace(LEGAL_SUFFIX_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Inline ▲/▼ badge for "above/below industry median". Null when unknown.
 *  Wrapped in the themed Tooltip so the explanation matches the rest of the
 *  application chrome rather than the browser's default OS-level title popup. */
function MedianFlag({ above }: { above: boolean | null | undefined }) {
  if (above === null || above === undefined) return null;
  return above ? (
    <Tooltip content="Above industry median" position="top">
      <span
        className="inline-flex items-center ml-1.5 text-[10px] font-medium text-emerald-700"
        tabIndex={0}
        aria-label="Above industry median"
      >
        <TrendingUp className="w-3 h-3" />
      </span>
    </Tooltip>
  ) : (
    <Tooltip content="Below industry median" position="top">
      <span
        className="inline-flex items-center ml-1.5 text-[10px] font-medium text-red-600"
        tabIndex={0}
        aria-label="Below industry median"
      >
        <TrendingDown className="w-3 h-3" />
      </span>
    </Tooltip>
  );
}

const PeerFinancialTable: React.FC<PeerFinancialTableProps> = ({
  competitors,
  focalFinancials,
  focalName,
  industryId,
  country,
}) => {
  const region = regionFromCountry(country);
  const renderRevenue = (v: number | null | undefined): string =>
    region ? formatCurrencyValue(v, region, /* compact */ true) : fmtLarge(v);

  // Industry median lookup — same query key as IndustryBenchmarksPanel so the
  // cache is shared. When industryId is missing or no peers are linked to
  // Company Research, flagsByName stays empty and MedianFlag silently no-ops.
  const { data: benchmarks } = useIndustryBenchmarks(industryId ?? null, 10, country ?? null);
  const flagsByName = useMemo<Map<string, CompanyFinancialSummary>>(() => {
    const map = new Map<string, CompanyFinancialSummary>();
    benchmarks?.company_details?.forEach((co) => {
      if (co.company_name) map.set(normalizeCompanyName(co.company_name), co);
    });
    return map;
  }, [benchmarks?.company_details]);
  const flagFor = (name: string | null | undefined) =>
    name ? flagsByName.get(normalizeCompanyName(name)) : undefined;

  const enriched = competitors.filter((c) => !!c.financials);
  const missing = competitors.filter((c) => !c.financials);

  const averages = useMemo(() => {
    if (enriched.length === 0) return null;
    const mean = (key: 'revenue' | 'gross_margin' | 'operating_margin'): number | null => {
      const vals = enriched
        .map((c) => c.financials?.[key])
        .filter((v): v is number => v !== null && v !== undefined && !Number.isNaN(v));
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    };
    return {
      revenue: mean('revenue'),
      gross_margin: mean('gross_margin'),
      operating_margin: mean('operating_margin'),
    };
  }, [enriched]);

  if (competitors.length === 0) return null;

  return (
    <Card className="p-5 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-primary-600" />
        <h4 className="text-sm font-semibold text-neutral-900">
          Peer Financial Comparison
        </h4>
        <span className="text-xs text-neutral-500">
          {enriched.length} of {competitors.length} peers linked to Company Research
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left">
              <th className="py-2 pr-3 font-medium text-neutral-600">Company</th>
              <th className="py-2 pr-3 font-medium text-neutral-600">Position</th>
              <th className="py-2 pr-3 font-medium text-neutral-600 text-right">Revenue</th>
              <th className="py-2 pr-3 font-medium text-neutral-600 text-right">Gross Margin</th>
              <th className="py-2 pr-3 font-medium text-neutral-600 text-right">Operating Margin</th>
              <th className="py-2 pr-3 font-medium text-neutral-600">Period</th>
            </tr>
          </thead>
          <tbody>
            {focalFinancials && focalName && (() => {
              const focalFlags = flagFor(focalName);
              return (
                <tr className="border-b border-neutral-100 bg-primary-50">
                  <td className="py-2 pr-3 font-medium text-primary-700">
                    {focalName} <span className="text-xs">(focal)</span>
                  </td>
                  <td className="py-2 pr-3 text-neutral-700">—</td>
                  <td className="py-2 pr-3 text-right text-neutral-900">
                    <span className="inline-flex items-center justify-end">
                      {renderRevenue(focalFinancials.revenue)}
                      <MedianFlag above={focalFlags?.above_median_revenue} />
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right text-neutral-900">{fmtPct(focalFinancials.grossMargin)}</td>
                  <td className="py-2 pr-3 text-right text-neutral-900">{fmtPct(focalFinancials.operatingMargin)}</td>
                  <td className="py-2 pr-3 text-neutral-600 text-xs">{focalFinancials.period || '—'}</td>
                </tr>
              );
            })()}
            {enriched.map((c) => {
              const peerFlags = flagFor(c.name);
              return (
                <tr key={c.competitorId} className="border-b border-neutral-100">
                  <td className="py-2 pr-3 text-neutral-900">{c.name}</td>
                  <td className="py-2 pr-3 text-neutral-600 text-xs">{c.market_position ?? '—'}</td>
                  <td className="py-2 pr-3 text-right text-neutral-900">
                    <span className="inline-flex items-center justify-end">
                      {renderRevenue(c.financials?.revenue)}
                      <MedianFlag above={peerFlags?.above_median_revenue} />
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-right text-neutral-900">{fmtPct(c.financials?.gross_margin)}</td>
                  <td className="py-2 pr-3 text-right text-neutral-900">{fmtPct(c.financials?.operating_margin)}</td>
                  <td className="py-2 pr-3 text-neutral-600 text-xs">{c.financials?.fiscal_period ?? '—'}</td>
                </tr>
              );
            })}
            {averages && (
              <tr className="border-b border-neutral-200 bg-neutral-50 font-medium">
                <td className="py-2 pr-3 text-neutral-700">Peer Average</td>
                <td className="py-2 pr-3 text-neutral-500 text-xs">{enriched.length} peers</td>
                <td className="py-2 pr-3 text-right text-neutral-700">{renderRevenue(averages.revenue)}</td>
                <td className="py-2 pr-3 text-right text-neutral-700">{fmtPct(averages.gross_margin)}</td>
                <td className="py-2 pr-3 text-right text-neutral-700">{fmtPct(averages.operating_margin)}</td>
                <td />
              </tr>
            )}
            {missing.map((c) => (
              <tr key={c.competitorId} className="border-b border-neutral-100 text-neutral-400">
                <td className="py-2 pr-3 italic">{c.name}</td>
                <td className="py-2 pr-3 text-xs">{c.market_position ?? '—'}</td>
                <td className="py-2 pr-3 text-right">—</td>
                <td className="py-2 pr-3 text-right">—</td>
                <td className="py-2 pr-3 text-right">—</td>
                <td className="py-2 pr-3">
                  {industryId ? (
                    <button
                      onClick={() => window.open(`/industries/${industryId}`, '_blank', 'noopener')}
                      className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      Add to Company Research
                    </button>
                  ) : (
                    <span className="text-xs">no data</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {missing.length > 0 && industryId && (
        <p className="text-xs text-neutral-500 mt-2 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" />
          Link peers to Company Research from the industry's competitive-landscape view to
          unlock financial comparison.
        </p>
      )}
    </Card>
  );
};

export default PeerFinancialTable;
